import { ChangeEvent, useState } from "react";
import {
  useAddChildCollectionMutation,
  useCreateCollectionMutation,
} from "@/api/api";
import { Plus } from "lucide-react";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog";
import { Input } from "../input";
import { Checkbox } from "../checkbox";
import { Field, FieldLabel } from "../field";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { SidebarGroupAction } from "../sidebar";
import { useIsOwnSpace } from "@/api/hooks";
import { HGCollection } from "@/common/types";

export default function CollectionCreate({
  className,
  parent,
}: {
  /** Laid out in the flow of the list header, not in `SidebarGroupAction`'s
   * absolute corner slot — the header owns its own row of controls. */
  className?: string;
  /**
   * What to offer to file the new collection under, or null for none. Decided
   * by the list rather than read from the URL here: what makes sense is the
   * collection the user is *looking at*, which is only sometimes the open one.
   */
  parent?: HGCollection | null;
}) {
  const [open, setOpen] = useState(false);
  const me = useAppSelector(selectMe);
  const isOwnSpace = useIsOwnSpace();

  const [createCollection] = useCreateCollectionMutation();
  const [addChildCollection] = useAddChildCollectionMutation();
  const [title, setTitle] = useState<string>("");
  const [asChild, setAsChild] = useState(true);

  if (!me || !isOwnSpace) return null;

  const handleCreate = async () => {
    if (title.trim().length === 0) return;
    try {
      const { id } = await createCollection({ title }).unwrap();
      if (asChild && parent) {
        await addChildCollection({
          parentID: parent.id,
          childIDs: [id],
        });
      }
    } catch {
      // No error UI here -- the dialog closes either way; a failed create
      // just means nothing new shows up.
    }
    setTitle("");
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setTitle("");
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <SidebarGroupAction className={className}>
          <Plus />
        </SidebarGroupAction>
      </DialogTrigger>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
        >
          <DialogHeader>
            <DialogTitle>New collection</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Field>
              <FieldLabel htmlFor="collectionTitle">Name</FieldLabel>
              <Input
                id="collectionTitle"
                value={title}
                placeholder="Enter collection name"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setTitle(e.target.value)
                }
                autoFocus
              />
            </Field>

            {parent && (
              <Field orientation="horizontal" className="mt-3">
                <Checkbox
                  id="collectionAsChild"
                  checked={asChild}
                  onCheckedChange={(checked) => setAsChild(checked === true)}
                />
                <FieldLabel htmlFor="collectionAsChild" className="font-normal">
                  Make child of &ldquo;{parent.title}&rdquo;
                </FieldLabel>
              </Field>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={title.trim().length === 0}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
